import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen, Users, Award, Clock, ChevronRight, Search,
  GraduationCap, Target, CheckCircle, Star, Filter,
  Play, FileText, Video, BarChart2, Shield,
  Zap, Globe, ArrowLeft, BookMarked, UserCheck, TrendingUp
} from 'lucide-react';


// ─── Types ────────────────────────────────────────────────────────────────────
interface TrainingModule {
  id: string;
  title: string;
  titleAr: string;
  duration: string;
  durationHours: number;
  category: string;
  categoryAr: string;
  type: 'video' | 'reading' | 'practice' | 'assessment';
  level: 'foundation' | 'intermediate' | 'advanced';
  roles: string[];
  description: string;
  descriptionAr: string;
  topics: string[];
  topicsAr: string[];
}

interface RolePath {
  roleId: string;
  roleTitle: string;
  roleTitleAr: string;
  group: string;
  totalHours: number;
  modules: string[];
  certifications: string[];
  certificationsAr: string[];
}

// ─── Training Modules Data (wired from roles-responsibilities.md) ─────────────
const TRAINING_MODULES: TrainingModule[] = [
  {
    id: 'tm-crm-fundamentals',
    title: 'CRM Fundamentals',
    titleAr: 'أساسيات نظام إدارة علاقات العملاء',
    duration: '8 hours',
    durationHours: 8,
    category: 'System',
    categoryAr: 'النظام',
    type: 'video',
    level: 'foundation',
    roles: ['bdc_agent', 'bdc_supervisor', 'sales_advisor', 'sales_manager', 'crm_admin'],
    description: 'Complete introduction to the CRM system: navigation, lead entry, field requirements, and daily workflows.',
    descriptionAr: 'مقدمة شاملة لنظام إدارة علاقات العملاء: التنقل، إدخال العملاء المحتملين، متطلبات الحقول، وسير العمل اليومي.',
    topics: ['CRM navigation', 'Lead creation', 'Field requirements', 'Stage transitions', 'Daily task management'],
    topicsAr: ['التنقل في CRM', 'إنشاء العملاء المحتملين', 'متطلبات الحقول', 'انتقالات المراحل', 'إدارة المهام اليومية']
  },
  {
    id: 'tm-call-scripts',
    title: 'Call Scripts & Objection Handling',
    titleAr: 'نصوص المكالمات والتعامل مع الاعتراضات',
    duration: '4 hours',
    durationHours: 4,
    category: 'Communication',
    categoryAr: 'التواصل',
    type: 'practice',
    level: 'foundation',
    roles: ['bdc_agent', 'bdc_supervisor', 'sales_advisor'],
    description: 'Approved call scripts for each pipeline stage, objection handling techniques, and escalation protocols.',
    descriptionAr: 'نصوص المكالمات المعتمدة لكل مرحلة من مراحل المسار، وتقنيات التعامل مع الاعتراضات، وبروتوكولات التصعيد.',
    topics: ['Opening scripts', 'Qualification questions', 'Objection responses', 'Closing techniques', 'Escalation triggers'],
    topicsAr: ['نصوص الافتتاح', 'أسئلة التأهيل', 'ردود على الاعتراضات', 'تقنيات الإغلاق', 'محفزات التصعيد']
  },
  {
    id: 'tm-whatsapp-etiquette',
    title: 'WhatsApp Business Etiquette',
    titleAr: 'آداب واتساب للأعمال',
    duration: '2 hours',
    durationHours: 2,
    category: 'Communication',
    categoryAr: 'التواصل',
    type: 'reading',
    level: 'foundation',
    roles: ['bdc_agent', 'bdc_supervisor', 'sales_advisor', 'delivery_coordinator'],
    description: 'WhatsApp Business API usage: approved templates, consent management, response timing, and compliance.',
    descriptionAr: 'استخدام WhatsApp Business API: القوالب المعتمدة، إدارة الموافقة، توقيت الاستجابة، والامتثال.',
    topics: ['Approved templates', 'PDPL consent', 'Response SLAs', 'Message formatting', 'Do\'s and don\'ts'],
    topicsAr: ['القوالب المعتمدة', 'موافقة PDPL', 'اتفاقيات مستوى الخدمة', 'تنسيق الرسائل', 'ما يجب وما لا يجب فعله']
  },
  {
    id: 'tm-product-knowledge',
    title: 'Product Knowledge – All Brands',
    titleAr: 'المعرفة بالمنتج - جميع العلامات التجارية',
    duration: '16 hours',
    durationHours: 16,
    category: 'Product',
    categoryAr: 'المنتج',
    type: 'video',
    level: 'foundation',
    roles: ['bdc_agent', 'bdc_supervisor', 'sales_advisor', 'sales_manager', 'captain'],
    description: 'Comprehensive product training covering Changan, Hyundai, and Stellantis vehicle lineups, features, and competitive positioning.',
    descriptionAr: 'تدريب شامل على المنتجات يغطي تشكيلة سيارات شانغان وهيونداي وستيلانتيس وميزاتها وتموضعها التنافسي.',
    topics: ['Changan lineup', 'Hyundai lineup', 'Stellantis lineup', 'Feature highlights', 'Competitive comparison'],
    topicsAr: ['تشكيلة شانغان', 'تشكيلة هيونداي', 'تشكيلة ستيلانتيس', 'أبرز الميزات', 'المقارنة التنافسية']
  },
  {
    id: 'tm-pdpl-compliance',
    title: 'PDPL Compliance & Data Privacy',
    titleAr: 'الامتثال لنظام حماية البيانات والخصوصية',
    duration: '2 hours',
    durationHours: 2,
    category: 'Compliance',
    categoryAr: 'الامتثال',
    type: 'assessment',
    level: 'foundation',
    roles: ['bdc_agent', 'bdc_supervisor', 'sales_advisor', 'sales_manager', 'crm_admin', 'quality_analyst'],
    description: 'Saudi PDPL requirements, customer data handling, consent management, and breach reporting procedures.',
    descriptionAr: 'متطلبات نظام حماية البيانات السعودي، التعامل مع بيانات العملاء، إدارة الموافقة، وإجراءات الإبلاغ عن الانتهاكات.',
    topics: ['PDPL overview', 'Consent requirements', 'Data retention', 'Breach procedures', 'Customer rights'],
    topicsAr: ['نظرة عامة على PDPL', 'متطلبات الموافقة', 'الاحتفاظ بالبيانات', 'إجراءات الانتهاك', 'حقوق العملاء']
  },
  {
    id: 'tm-leadership-coaching',
    title: 'Leadership & Coaching Skills',
    titleAr: 'مهارات القيادة والتوجيه',
    duration: '8 hours',
    durationHours: 8,
    category: 'Management',
    categoryAr: 'الإدارة',
    type: 'practice',
    level: 'intermediate',
    roles: ['bdc_supervisor', 'sales_manager'],
    description: 'Team leadership fundamentals, performance coaching, feedback delivery, and motivational techniques for automotive sales teams.',
    descriptionAr: 'أساسيات قيادة الفريق، التوجيه في الأداء، تقديم التغذية الراجعة، وتقنيات التحفيز لفرق مبيعات السيارات.',
    topics: ['Leadership styles', '1-on-1 coaching', 'Performance feedback', 'Team motivation', 'Conflict resolution'],
    topicsAr: ['أساليب القيادة', 'التوجيه الفردي', 'ملاحظات الأداء', 'تحفيز الفريق', 'حل النزاعات']
  },
  {
    id: 'tm-performance-management',
    title: 'Performance Management & KPIs',
    titleAr: 'إدارة الأداء ومؤشرات الأداء الرئيسية',
    duration: '4 hours',
    durationHours: 4,
    category: 'Management',
    categoryAr: 'الإدارة',
    type: 'reading',
    level: 'intermediate',
    roles: ['bdc_supervisor', 'sales_manager', 'quality_analyst'],
    description: 'KPI framework, scorecard usage, performance review cycles, and data-driven decision making for lead management.',
    descriptionAr: 'إطار مؤشرات الأداء الرئيسية، استخدام بطاقة الأداء، دورات مراجعة الأداء، واتخاذ القرارات المبنية على البيانات.',
    topics: ['KPI definitions', 'Scorecard reading', 'Review cycles', 'Improvement plans', 'Dashboard usage'],
    topicsAr: ['تعريفات KPI', 'قراءة بطاقة الأداء', 'دورات المراجعة', 'خطط التحسين', 'استخدام لوحة المعلومات']
  },
  {
    id: 'tm-sales-process',
    title: 'Sales Process & Techniques',
    titleAr: 'عملية المبيعات والتقنيات',
    duration: '16 hours',
    durationHours: 16,
    category: 'Sales',
    categoryAr: 'المبيعات',
    type: 'practice',
    level: 'intermediate',
    roles: ['sales_advisor', 'sales_manager'],
    description: 'Complete sales methodology from lead qualification through delivery: needs analysis, test drive, quote presentation, negotiation, and closing.',
    descriptionAr: 'منهجية المبيعات الكاملة من تأهيل العميل المحتمل حتى التسليم: تحليل الاحتياجات، قيادة تجريبية، عرض الأسعار، التفاوض، والإغلاق.',
    topics: ['Needs analysis', 'Test drive protocol', 'Quote presentation', 'Negotiation tactics', 'Closing methods'],
    topicsAr: ['تحليل الاحتياجات', 'بروتوكول القيادة التجريبية', 'عرض الأسعار', 'تكتيكات التفاوض', 'طرق الإغلاق']
  },
  {
    id: 'tm-finance-insurance',
    title: 'Finance & Insurance Basics',
    titleAr: 'أساسيات التمويل والتأمين',
    duration: '4 hours',
    durationHours: 4,
    category: 'Finance',
    categoryAr: 'التمويل',
    type: 'reading',
    level: 'foundation',
    roles: ['sales_advisor', 'sales_manager', 'finance_officer'],
    description: 'Vehicle financing options, lease vs. buy analysis, insurance products, and F&I compliance requirements.',
    descriptionAr: 'خيارات تمويل السيارات، تحليل الإيجار مقابل الشراء، منتجات التأمين، ومتطلبات الامتثال لـ F&I.',
    topics: ['Financing products', 'Lease structures', 'Insurance types', 'F&I compliance', 'Documentation'],
    topicsAr: ['منتجات التمويل', 'هياكل الإيجار', 'أنواع التأمين', 'امتثال F&I', 'التوثيق']
  },
  {
    id: 'tm-negotiation-skills',
    title: 'Negotiation Skills',
    titleAr: 'مهارات التفاوض',
    duration: '8 hours',
    durationHours: 8,
    category: 'Sales',
    categoryAr: 'المبيعات',
    type: 'practice',
    level: 'advanced',
    roles: ['sales_advisor', 'sales_manager'],
    description: 'Advanced negotiation frameworks, trade-in handling, discount authority, and win-win closing strategies.',
    descriptionAr: 'أطر التفاوض المتقدمة، التعامل مع المقايضة، سلطة الخصم، واستراتيجيات الإغلاق المربحة للجميع.',
    topics: ['Negotiation frameworks', 'Trade-in tactics', 'Discount management', 'Win-win closing', 'Escalation authority'],
    topicsAr: ['أطر التفاوض', 'تكتيكات المقايضة', 'إدارة الخصم', 'الإغلاق المربح للجميع', 'سلطة التصعيد']
  },
  {
    id: 'tm-driving-advanced',
    title: 'Advanced Driving Course',
    titleAr: 'دورة القيادة المتقدمة',
    duration: '16 hours',
    durationHours: 16,
    category: 'Operations',
    categoryAr: 'العمليات',
    type: 'practice',
    level: 'intermediate',
    roles: ['captain'],
    description: 'Advanced vehicle handling, safety protocols, feature demonstration techniques, and test drive route management.',
    descriptionAr: 'التعامل المتقدم مع المركبة، بروتوكولات السلامة، تقنيات عرض الميزات، وإدارة مسار القيادة التجريبية.',
    topics: ['Vehicle dynamics', 'Safety protocols', 'Feature demonstration', 'Route planning', 'Emergency procedures'],
    topicsAr: ['ديناميكيات المركبة', 'بروتوكولات السلامة', 'عرض الميزات', 'تخطيط المسار', 'إجراءات الطوارئ']
  },
  {
    id: 'tm-crm-admin',
    title: 'Advanced CRM Administration',
    titleAr: 'إدارة CRM المتقدمة',
    duration: '4 hours',
    durationHours: 4,
    category: 'System',
    categoryAr: 'النظام',
    type: 'video',
    level: 'advanced',
    roles: ['crm_admin', 'bdc_supervisor'],
    description: 'CRM system administration: user management, field configuration, automation rules, reporting, and integration management.',
    descriptionAr: 'إدارة نظام CRM: إدارة المستخدمين، تكوين الحقول، قواعد الأتمتة، إعداد التقارير، وإدارة التكامل.',
    topics: ['User management', 'Field configuration', 'Automation setup', 'Report building', 'Integration config'],
    topicsAr: ['إدارة المستخدمين', 'تكوين الحقول', 'إعداد الأتمتة', 'بناء التقارير', 'تكوين التكامل']
  },
  {
    id: 'tm-quality-audit',
    title: 'Quality Audit & Compliance',
    titleAr: 'مراجعة الجودة والامتثال',
    duration: '8 hours',
    durationHours: 8,
    category: 'Quality',
    categoryAr: 'الجودة',
    type: 'assessment',
    level: 'advanced',
    roles: ['quality_analyst', 'bdc_supervisor', 'sales_manager'],
    description: 'Quality review methodology, call monitoring, CRM data audit, KPI calculation, and improvement plan development.',
    descriptionAr: 'منهجية مراجعة الجودة، مراقبة المكالمات، تدقيق بيانات CRM، حساب مؤشرات الأداء، وتطوير خطط التحسين.',
    topics: ['Audit methodology', 'Call monitoring', 'CRM data review', 'KPI calculation', 'Report writing'],
    topicsAr: ['منهجية التدقيق', 'مراقبة المكالمات', 'مراجعة بيانات CRM', 'حساب KPI', 'كتابة التقارير']
  },
  {
    id: 'tm-delivery-process',
    title: 'Delivery Process & Customer Experience',
    titleAr: 'عملية التسليم وتجربة العملاء',
    duration: '8 hours',
    durationHours: 8,
    category: 'Operations',
    categoryAr: 'العمليات',
    type: 'practice',
    level: 'intermediate',
    roles: ['delivery_coordinator', 'sales_advisor'],
    description: 'Vehicle PDI checklist, delivery ceremony, documentation workflow, customer handover protocols, and post-sale follow-up.',
    descriptionAr: 'قائمة فحص PDI للمركبة، احتفالية التسليم، سير عمل التوثيق، بروتوكولات تسليم العميل، ومتابعة ما بعد البيع.',
    topics: ['PDI checklist', 'Delivery ceremony', 'Documentation', 'Customer handover', 'Post-sale follow-up'],
    topicsAr: ['قائمة فحص PDI', 'احتفالية التسليم', 'التوثيق', 'تسليم العميل', 'متابعة ما بعد البيع']
  },
  {
    id: 'tm-trade-in-valuation',
    title: 'Trade-in Valuation Techniques',
    titleAr: 'تقنيات تقييم المقايضة',
    duration: '8 hours',
    durationHours: 8,
    category: 'Finance',
    categoryAr: 'التمويل',
    type: 'practice',
    level: 'intermediate',
    roles: ['trade_in_evaluator', 'sales_advisor', 'sales_manager'],
    description: 'Vehicle condition assessment, market valuation tools, trade-in negotiation, and approval workflow for above-market offers.',
    descriptionAr: 'تقييم حالة المركبة، أدوات تقييم السوق، التفاوض على المقايضة، وسير عمل الموافقة للعروض فوق السوق.',
    topics: ['Condition assessment', 'Market tools', 'Valuation formula', 'Negotiation', 'Approval process'],
    topicsAr: ['تقييم الحالة', 'أدوات السوق', 'صيغة التقييم', 'التفاوض', 'عملية الموافقة']
  },
  {
    id: 'tm-customer-service',
    title: 'Customer Service Excellence',
    titleAr: 'التميز في خدمة العملاء',
    duration: '8 hours',
    durationHours: 8,
    category: 'Communication',
    categoryAr: 'التواصل',
    type: 'practice',
    level: 'foundation',
    roles: ['bdc_agent', 'sales_advisor', 'captain', 'delivery_coordinator'],
    description: 'Customer journey mapping, complaint handling, NPS improvement, cultural sensitivity, and bilingual service standards.',
    descriptionAr: 'رسم خريطة رحلة العميل، التعامل مع الشكاوى، تحسين NPS، الحساسية الثقافية، ومعايير الخدمة ثنائية اللغة.',
    topics: ['Customer journey', 'Complaint handling', 'NPS improvement', 'Cultural sensitivity', 'Bilingual standards'],
    topicsAr: ['رحلة العميل', 'التعامل مع الشكاوى', 'تحسين NPS', 'الحساسية الثقافية', 'معايير ثنائية اللغة']
  }
];

// ─── Role Learning Paths ───────────────────────────────────────────────────────
const ROLE_PATHS: RolePath[] = [
  {
    roleId: 'bdc_agent',
    roleTitle: 'BDC Agent',
    roleTitleAr: 'وكيل مركز تطوير الأعمال',
    group: 'bdc',
    totalHours: 32,
    modules: ['tm-crm-fundamentals', 'tm-call-scripts', 'tm-whatsapp-etiquette', 'tm-product-knowledge', 'tm-pdpl-compliance', 'tm-customer-service'],
    certifications: ['CRM Certified User', 'PDPL Data Compliance', 'Bilingual Communication'],
    certificationsAr: ['مستخدم CRM معتمد', 'امتثال بيانات PDPL', 'التواصل ثنائي اللغة']
  },
  {
    roleId: 'bdc_supervisor',
    roleTitle: 'BDC Supervisor',
    roleTitleAr: 'مشرف مركز تطوير الأعمال',
    group: 'bdc',
    totalHours: 56,
    modules: ['tm-crm-fundamentals', 'tm-call-scripts', 'tm-whatsapp-etiquette', 'tm-product-knowledge', 'tm-pdpl-compliance', 'tm-leadership-coaching', 'tm-performance-management', 'tm-crm-admin', 'tm-quality-audit'],
    certifications: ['CRM Certified Administrator', 'Team Leadership Certificate', 'Quality Auditor'],
    certificationsAr: ['مسؤول CRM معتمد', 'شهادة قيادة الفريق', 'مدقق الجودة']
  },
  {
    roleId: 'sales_advisor',
    roleTitle: 'Sales Advisor',
    roleTitleAr: 'مستشار المبيعات',
    group: 'sales',
    totalHours: 68,
    modules: ['tm-crm-fundamentals', 'tm-product-knowledge', 'tm-pdpl-compliance', 'tm-sales-process', 'tm-finance-insurance', 'tm-negotiation-skills', 'tm-customer-service', 'tm-delivery-process'],
    certifications: ['Certified Sales Professional', 'Product Knowledge – All Brands', 'Customer Experience Excellence'],
    certificationsAr: ['محترف مبيعات معتمد', 'معرفة المنتج - جميع العلامات التجارية', 'التميز في تجربة العملاء']
  },
  {
    roleId: 'sales_manager',
    roleTitle: 'Sales Manager',
    roleTitleAr: 'مدير المبيعات',
    group: 'sales',
    totalHours: 64,
    modules: ['tm-crm-fundamentals', 'tm-product-knowledge', 'tm-pdpl-compliance', 'tm-leadership-coaching', 'tm-performance-management', 'tm-sales-process', 'tm-negotiation-skills', 'tm-quality-audit', 'tm-finance-insurance'],
    certifications: ['Sales Manager Certification', 'Performance Coaching Badge', 'Advanced Negotiation'],
    certificationsAr: ['شهادة مدير المبيعات', 'شارة التوجيه في الأداء', 'التفاوض المتقدم']
  },
  {
    roleId: 'captain',
    roleTitle: 'Captain (Test Drive Specialist)',
    roleTitleAr: 'كابتن (أخصائي القيادة التجريبية)',
    group: 'sales',
    totalHours: 48,
    modules: ['tm-product-knowledge', 'tm-pdpl-compliance', 'tm-driving-advanced', 'tm-customer-service'],
    certifications: ['Advanced Driving Certificate', 'Vehicle Demonstration Specialist', 'Safety Compliance'],
    certificationsAr: ['شهادة القيادة المتقدمة', 'أخصائي عرض المركبات', 'امتثال السلامة']
  },
  {
    roleId: 'finance_officer',
    roleTitle: 'Finance Officer',
    roleTitleAr: 'مسؤول التمويل',
    group: 'finance',
    totalHours: 20,
    modules: ['tm-finance-insurance', 'tm-pdpl-compliance', 'tm-crm-fundamentals'],
    certifications: ['F&I Certification', 'Compliance Specialist', 'CRM Financial Module'],
    certificationsAr: ['شهادة F&I', 'أخصائي الامتثال', 'وحدة CRM المالية']
  },
  {
    roleId: 'trade_in_evaluator',
    roleTitle: 'Trade-in Evaluator',
    roleTitleAr: 'مقيم المقايضة',
    group: 'finance',
    totalHours: 26,
    modules: ['tm-trade-in-valuation', 'tm-product-knowledge', 'tm-pdpl-compliance'],
    certifications: ['Vehicle Valuation Specialist', 'Market Analysis Certificate'],
    certificationsAr: ['أخصائي تقييم المركبات', 'شهادة تحليل السوق']
  },
  {
    roleId: 'delivery_coordinator',
    roleTitle: 'Delivery Coordinator',
    roleTitleAr: 'منسق التسليم',
    group: 'operations',
    totalHours: 26,
    modules: ['tm-delivery-process', 'tm-whatsapp-etiquette', 'tm-pdpl-compliance', 'tm-customer-service'],
    certifications: ['Delivery Excellence Certificate', 'Customer Experience Badge'],
    certificationsAr: ['شهادة التميز في التسليم', 'شارة تجربة العملاء']
  },
  {
    roleId: 'crm_admin',
    roleTitle: 'CRM Administrator',
    roleTitleAr: 'مدير CRM',
    group: 'operations',
    totalHours: 22,
    modules: ['tm-crm-fundamentals', 'tm-crm-admin', 'tm-pdpl-compliance'],
    certifications: ['CRM System Administrator', 'Data Governance Certificate', 'Integration Specialist'],
    certificationsAr: ['مدير نظام CRM', 'شهادة حوكمة البيانات', 'أخصائي التكامل']
  },
  {
    roleId: 'quality_analyst',
    roleTitle: 'Quality Analyst',
    roleTitleAr: 'محلل الجودة',
    group: 'operations',
    totalHours: 22,
    modules: ['tm-pdpl-compliance', 'tm-performance-management', 'tm-quality-audit'],
    certifications: ['Quality Assurance Specialist', 'Data Analysis Certificate', 'Audit Methodology'],
    certificationsAr: ['أخصائي ضمان الجودة', 'شهادة تحليل البيانات', 'منهجية التدقيق']
  }
];

// ─── Helper functions ─────────────────────────────────────────────────────────
const categoryColors: Record<string, string> = {
  System: 'bg-blue-100 text-blue-800',
  Communication: 'bg-green-100 text-green-800',
  Compliance: 'bg-red-100 text-red-800',
  Product: 'bg-purple-100 text-purple-800',
  Management: 'bg-orange-100 text-orange-800',
  Sales: 'bg-yellow-100 text-yellow-800',
  Finance: 'bg-teal-100 text-teal-800',
  Quality: 'bg-indigo-100 text-indigo-800',
  Operations: 'bg-pink-100 text-pink-800'
};

const levelColors: Record<string, string> = {
  foundation: 'bg-emerald-100 text-emerald-800',
  intermediate: 'bg-amber-100 text-amber-800',
  advanced: 'bg-rose-100 text-rose-800'
};

const typeIcons: Record<string, React.ReactNode> = {
  video: <Video className="w-4 h-4" />,
  reading: <FileText className="w-4 h-4" />,
  practice: <Play className="w-4 h-4" />,
  assessment: <Award className="w-4 h-4" />
};

const groupColors: Record<string, string> = {
  bdc: 'border-l-4 border-blue-500',
  sales: 'border-l-4 border-emerald-500',
  finance: 'border-l-4 border-purple-500',
  operations: 'border-l-4 border-orange-500'
};

const groupBadgeColors: Record<string, string> = {
  bdc: 'bg-blue-100 text-blue-800',
  sales: 'bg-emerald-100 text-emerald-800',
  finance: 'bg-purple-100 text-purple-800',
  operations: 'bg-orange-100 text-orange-800'
};

// ─── Main Component ───────────────────────────────────────────────────────────
const TrainingResources = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'paths' | 'catalog' | 'certifications'>('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<string>('all');
  const [selectedLevel, setSelectedLevel] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [lang, setLang] = useState<'en' | 'ar'>('en');


  // Compute stats
  const totalHours = TRAINING_MODULES.reduce((sum, m) => sum + m.durationHours, 0);
  const totalModules = TRAINING_MODULES.length;
  const totalCertifications = [...new Set(ROLE_PATHS.flatMap(p => p.certifications))].length;
  const totalRolePaths = ROLE_PATHS.length;

  // Filter modules
  const filteredModules = TRAINING_MODULES.filter(m => {
    const matchSearch = !searchQuery ||
      m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.titleAr.includes(searchQuery) ||
      m.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchLevel = selectedLevel === 'all' || m.level === selectedLevel;
    const matchCategory = selectedCategory === 'all' || m.category === selectedCategory;
    return matchSearch && matchLevel && matchCategory;
  });

  // Filter paths
  const filteredPaths = ROLE_PATHS.filter(p => {
    const matchGroup = selectedGroup === 'all' || p.group === selectedGroup;
    const matchSearch = !searchQuery ||
      p.roleTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.roleTitleAr.includes(searchQuery);
    return matchGroup && matchSearch;
  });

  const categories = [...new Set(TRAINING_MODULES.map(m => m.category))];

  const selectedRolePath = selectedRole ? ROLE_PATHS.find(p => p.roleId === selectedRole) : null;
  const selectedRoleModules = selectedRolePath
    ? TRAINING_MODULES.filter(m => selectedRolePath.modules.includes(m.id))
    : [];

  return (
    <div className={`min-h-screen bg-gray-50 ${lang === 'ar' ? 'rtl font-arabic' : 'ltr'}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-700 via-indigo-600 to-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between mb-4">
            <Link to="/" className="flex items-center gap-2 text-indigo-200 hover:text-white text-sm transition-colors">
              <ArrowLeft className="w-4 h-4" />
              {lang === 'ar' ? 'الرئيسية' : 'Home'}
            </Link>
            <button
              onClick={() => setLang(l => l === 'en' ? 'ar' : 'en')}
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg text-sm transition-colors"
            >
              <Globe className="w-4 h-4" />
              {lang === 'en' ? 'العربية' : 'English'}
            </button>
          </div>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
              <GraduationCap className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">
                {lang === 'ar' ? 'التدريب والموارد' : 'Training & Resources'}
              </h1>
              <p className="text-indigo-200 mt-1">
                {lang === 'ar'
                  ? 'مسارات التعلم حسب الدور · كتالوج الوحدات · الشهادات'
                  : 'Role-based learning paths · Module catalog · Certifications'}
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: lang === 'ar' ? 'وحدة تدريبية' : 'Training Modules', value: totalModules, icon: <BookOpen className="w-5 h-5" /> },
              { label: lang === 'ar' ? 'ساعة تدريب' : 'Training Hours', value: totalHours, icon: <Clock className="w-5 h-5" /> },
              { label: lang === 'ar' ? 'مسار حسب الدور' : 'Role Paths', value: totalRolePaths, icon: <Users className="w-5 h-5" /> },
              { label: lang === 'ar' ? 'شهادة متاحة' : 'Certifications', value: totalCertifications, icon: <Award className="w-5 h-5" /> }
            ].map((stat, i) => (
              <div key={i} className="bg-white/15 backdrop-blur rounded-xl p-4 flex items-center gap-3">
                <div className="text-indigo-200">{stat.icon}</div>
                <div>
                  <div className="text-2xl font-bold">{stat.value}</div>
                  <div className="text-indigo-200 text-xs">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1 overflow-x-auto">
            {[
              { id: 'overview', label: lang === 'ar' ? 'نظرة عامة' : 'Overview', icon: <BarChart2 className="w-4 h-4" /> },
              { id: 'paths', label: lang === 'ar' ? 'المسارات حسب الدور' : 'Role Paths', icon: <UserCheck className="w-4 h-4" /> },
              { id: 'catalog', label: lang === 'ar' ? 'كتالوج الوحدات' : 'Module Catalog', icon: <BookMarked className="w-4 h-4" /> },
              { id: 'certifications', label: lang === 'ar' ? 'الشهادات' : 'Certifications', icon: <Award className="w-4 h-4" /> }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={`flex items-center gap-2 px-5 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Search Bar */}
        <div className="mb-6 flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={lang === 'ar' ? 'ابحث عن وحدة أو دور...' : 'Search modules, roles...'}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* ── OVERVIEW TAB ── */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Quick Navigation */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {lang === 'ar' ? 'التنقل السريع' : 'Quick Navigation'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  {
                    title: lang === 'ar' ? 'مسارات التعلم حسب الدور' : 'Role-Based Learning Paths',
                    desc: lang === 'ar' ? 'خطط تدريب مخصصة لكل دور في الفريق' : 'Tailored training plans for each team role',
                    icon: <UserCheck className="w-8 h-8 text-indigo-600" />,
                    tab: 'paths',
                    color: 'from-indigo-50 to-indigo-100 border-indigo-200'
                  },
                  {
                    title: lang === 'ar' ? 'كتالوج الوحدات' : 'Module Catalog',
                    desc: lang === 'ar' ? `${totalModules} وحدة عبر ${categories.length} فئات` : `${totalModules} modules across ${categories.length} categories`,
                    icon: <BookMarked className="w-8 h-8 text-emerald-600" />,
                    tab: 'catalog',
                    color: 'from-emerald-50 to-emerald-100 border-emerald-200'
                  },
                  {
                    title: lang === 'ar' ? 'الشهادات والمؤهلات' : 'Certifications & Badges',
                    desc: lang === 'ar' ? `${totalCertifications} شهادة متاحة عبر جميع المسارات` : `${totalCertifications} certifications available across all paths`,
                    icon: <Award className="w-8 h-8 text-amber-600" />,
                    tab: 'certifications',
                    color: 'from-amber-50 to-amber-100 border-amber-200'
                  }
                ].map((item, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveTab(item.tab as typeof activeTab)}
                    className={`bg-gradient-to-br ${item.color} border rounded-xl p-6 text-left hover:shadow-md transition-all`}
                  >
                    <div className="mb-3">{item.icon}</div>
                    <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                    <p className="text-sm text-gray-600">{item.desc}</p>
                    <div className="mt-3 flex items-center gap-1 text-indigo-600 text-sm font-medium">
                      {lang === 'ar' ? 'عرض' : 'View'} <ChevronRight className="w-4 h-4" />
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Training by Group */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {lang === 'ar' ? 'التدريب حسب المجموعة' : 'Training by Group'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  {
                    group: 'bdc',
                    label: lang === 'ar' ? 'مركز تطوير الأعمال' : 'Business Development Center',
                    color: 'blue',
                    paths: ROLE_PATHS.filter(p => p.group === 'bdc'),
                    focus: lang === 'ar' ? 'CRM، التواصل، الامتثال' : 'CRM, Communication, Compliance'
                  },
                  {
                    group: 'sales',
                    label: lang === 'ar' ? 'فريق المبيعات' : 'Sales Team',
                    color: 'emerald',
                    paths: ROLE_PATHS.filter(p => p.group === 'sales'),
                    focus: lang === 'ar' ? 'المنتج، المبيعات، التفاوض' : 'Product, Sales Process, Negotiation'
                  },
                  {
                    group: 'finance',
                    label: lang === 'ar' ? 'فريق التمويل' : 'Finance Team',
                    color: 'purple',
                    paths: ROLE_PATHS.filter(p => p.group === 'finance'),
                    focus: lang === 'ar' ? 'التمويل، التأمين، الامتثال' : 'Finance, Insurance, Compliance'
                  },
                  {
                    group: 'operations',
                    label: lang === 'ar' ? 'العمليات والجودة' : 'Operations & Quality',
                    color: 'orange',
                    paths: ROLE_PATHS.filter(p => p.group === 'operations'),
                    focus: lang === 'ar' ? 'التسليم، إدارة CRM، الجودة' : 'Delivery, CRM Admin, Quality'
                  }
                ].map((grp, i) => (
                  <div key={i} className={`bg-white border border-gray-200 rounded-xl p-5 border-l-4 border-l-${grp.color}-500`}>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-gray-900">{grp.label}</h3>
                      <span className={`text-xs px-2 py-1 rounded-full bg-${grp.color}-100 text-${grp.color}-800`}>
                        {grp.paths.length} {lang === 'ar' ? 'أدوار' : 'roles'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mb-3">{grp.focus}</p>
                    <div className="space-y-2">
                      {grp.paths.map(path => (
                        <div key={path.roleId} className="flex items-center justify-between text-sm">
                          <span className="text-gray-700">{lang === 'ar' ? path.roleTitleAr : path.roleTitle}</span>
                          <span className="text-gray-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {path.totalHours}h
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Category Breakdown */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {lang === 'ar' ? 'توزيع الوحدات حسب الفئة' : 'Module Distribution by Category'}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {categories.map(cat => {
                  const catModules = TRAINING_MODULES.filter(m => m.category === cat);
                  const catHours = catModules.reduce((s, m) => s + m.durationHours, 0);
                  return (
                    <div key={cat} className="bg-white border border-gray-200 rounded-xl p-4 text-center hover:shadow-sm transition-shadow cursor-pointer"
                      onClick={() => { setSelectedCategory(cat); setActiveTab('catalog'); }}>
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium mb-2 ${categoryColors[cat] || 'bg-gray-100 text-gray-800'}`}>
                        {cat}
                      </span>
                      <div className="text-2xl font-bold text-gray-900">{catModules.length}</div>
                      <div className="text-xs text-gray-500">{catHours}h total</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Governance Links */}
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-indigo-900 mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5" />
                {lang === 'ar' ? 'روابط الحوكمة' : 'Governance Links'}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: lang === 'ar' ? 'السياسات والحوكمة' : 'Policies & Governance', path: '/policies', icon: <Shield className="w-4 h-4" /> },
                  { label: lang === 'ar' ? 'تكوين CRM' : 'CRM Configuration', path: '/crm', icon: <Zap className="w-4 h-4" /> },
                  { label: lang === 'ar' ? 'دليل التنفيذ' : 'Implementation Guide', path: '/implementation', icon: <Target className="w-4 h-4" /> },
                  { label: lang === 'ar' ? 'الأدوار والمسؤوليات' : 'Roles & Responsibilities', path: '/roles', icon: <Users className="w-4 h-4" /> }
                ].map((link, i) => (
                  <Link key={i} to={link.path}
                    className="flex items-center gap-2 bg-white border border-indigo-100 rounded-lg px-3 py-2 text-sm text-indigo-700 hover:bg-indigo-50 transition-colors">
                    {link.icon}
                    {link.label}
                    <ChevronRight className="w-3 h-3 ml-auto" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── ROLE PATHS TAB ── */}
        {activeTab === 'paths' && (
          <div className="space-y-6">
            {/* Group Filter */}
            <div className="flex gap-2 flex-wrap">
              {['all', 'bdc', 'sales', 'finance', 'operations'].map(g => (
                <button
                  key={g}
                  onClick={() => setSelectedGroup(g)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    selectedGroup === g
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:border-indigo-400'
                  }`}
                >
                  {g === 'all' ? (lang === 'ar' ? 'الكل' : 'All Groups') : g.toUpperCase()}
                </button>
              ))}
            </div>

            {/* Role Path Cards */}
            {!selectedRole ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredPaths.map(path => (
                  <div
                    key={path.roleId}
                    className={`bg-white rounded-xl border border-gray-200 p-6 ${groupColors[path.group]} hover:shadow-md transition-shadow cursor-pointer`}
                    onClick={() => setSelectedRole(path.roleId)}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-gray-900 text-lg">
                          {lang === 'ar' ? path.roleTitleAr : path.roleTitle}
                        </h3>
                        <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${groupBadgeColors[path.group]}`}>
                          {path.group.toUpperCase()}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-gray-900">{path.totalHours}h</div>
                        <div className="text-xs text-gray-500">{lang === 'ar' ? 'إجمالي' : 'total'}</div>
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="flex items-center justify-between text-sm text-gray-500 mb-1">
                        <span>{lang === 'ar' ? 'الوحدات' : 'Modules'}</span>
                        <span>{path.modules.length}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div
                          className="bg-indigo-500 h-2 rounded-full"
                          style={{ width: `${Math.min((path.modules.length / 16) * 100, 100)}%` }}
                        />
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="text-xs font-medium text-gray-500 mb-2">
                        {lang === 'ar' ? 'الشهادات المتضمنة' : 'Certifications Included'}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {path.certifications.slice(0, 2).map((cert, i) => (
                          <span key={i} className="flex items-center gap-1 text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full border border-amber-200">
                            <Award className="w-3 h-3" />
                            {lang === 'ar' ? path.certificationsAr[i] : cert}
                          </span>
                        ))}
                        {path.certifications.length > 2 && (
                          <span className="text-xs text-gray-400">+{path.certifications.length - 2} more</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex flex-wrap gap-1">
                        {TRAINING_MODULES.filter(m => path.modules.includes(m.id)).slice(0, 3).map(m => (
                          <span key={m.id} className={`text-xs px-1.5 py-0.5 rounded ${categoryColors[m.category] || 'bg-gray-100 text-gray-600'}`}>
                            {lang === 'ar' ? m.categoryAr : m.category}
                          </span>
                        ))}
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Role Detail
              <div>
                <button
                  onClick={() => setSelectedRole(null)}
                  className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 text-sm mb-6"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {lang === 'ar' ? 'العودة إلى المسارات' : 'Back to Paths'}
                </button>

                {selectedRolePath && (
                  <div className="space-y-6">
                    {/* Role Header */}
                    <div className={`bg-white rounded-xl border border-gray-200 p-6 ${groupColors[selectedRolePath.group]}`}>
                      <div className="flex items-center justify-between mb-2">
                        <h2 className="text-2xl font-bold text-gray-900">
                          {lang === 'ar' ? selectedRolePath.roleTitleAr : selectedRolePath.roleTitle}
                        </h2>
                        <span className="text-3xl font-bold text-indigo-600">{selectedRolePath.totalHours}h</span>
                      </div>
                      <span className={`inline-block text-sm px-3 py-1 rounded-full ${groupBadgeColors[selectedRolePath.group]}`}>
                        {selectedRolePath.group.toUpperCase()} Group
                      </span>
                    </div>

                    {/* Certifications */}
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                      <h3 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                        <Award className="w-5 h-5" />
                        {lang === 'ar' ? 'الشهادات الممنوحة عند الإتمام' : 'Certifications Awarded on Completion'}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {selectedRolePath.certifications.map((cert, i) => (
                          <div key={i} className="bg-white border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                            <Star className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                            <span className="text-sm text-amber-900">
                              {lang === 'ar' ? selectedRolePath.certificationsAr[i] : cert}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Modules */}
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-4">
                        {lang === 'ar' ? 'وحدات التدريب المطلوبة' : 'Required Training Modules'}
                        <span className="ml-2 text-indigo-600">({selectedRoleModules.length})</span>
                      </h3>
                      <div className="space-y-3">
                        {selectedRoleModules.map((module, idx) => (
                          <div key={module.id} className="bg-white border border-gray-200 rounded-xl p-4 flex gap-4">
                            <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-bold text-sm shrink-0">
                              {idx + 1}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <h4 className="font-medium text-gray-900">
                                    {lang === 'ar' ? module.titleAr : module.title}
                                  </h4>
                                  <p className="text-sm text-gray-500 mt-1">
                                    {lang === 'ar' ? module.descriptionAr : module.description}
                                  </p>
                                </div>
                                <div className="text-right shrink-0">
                                  <div className="text-sm font-medium text-gray-700">{module.duration}</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 mt-2">
                                <span className={`text-xs px-2 py-0.5 rounded-full ${categoryColors[module.category] || 'bg-gray-100 text-gray-600'}`}>
                                  {lang === 'ar' ? module.categoryAr : module.category}
                                </span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${levelColors[module.level]}`}>
                                  {module.level}
                                </span>
                                <span className="flex items-center gap-1 text-xs text-gray-500">
                                  {typeIcons[module.type]}
                                  {module.type}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── MODULE CATALOG TAB ── */}
        {activeTab === 'catalog' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="flex gap-3 flex-wrap items-center">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Filter className="w-4 h-4" />
                {lang === 'ar' ? 'تصفية:' : 'Filter:'}
              </div>
              <div className="flex gap-2 flex-wrap">
                <select
                  value={selectedLevel}
                  onChange={e => setSelectedLevel(e.target.value)}
                  className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="all">{lang === 'ar' ? 'جميع المستويات' : 'All Levels'}</option>
                  <option value="foundation">Foundation</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
                <select
                  value={selectedCategory}
                  onChange={e => setSelectedCategory(e.target.value)}
                  className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="all">{lang === 'ar' ? 'جميع الفئات' : 'All Categories'}</option>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <span className="text-sm text-gray-500 ml-auto">
                {filteredModules.length} {lang === 'ar' ? 'وحدة' : 'modules'}
              </span>
            </div>

            {/* Module Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {filteredModules.map(module => (
                <div key={module.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">
                        {lang === 'ar' ? module.titleAr : module.title}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {lang === 'ar' ? module.descriptionAr : module.description}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-lg font-bold text-gray-700">{module.durationHours}h</div>
                      <div className="flex items-center gap-1 text-gray-400 text-xs justify-end">
                        {typeIcons[module.type]}
                        {module.type}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${categoryColors[module.category] || 'bg-gray-100 text-gray-600'}`}>
                      {lang === 'ar' ? module.categoryAr : module.category}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${levelColors[module.level]}`}>
                      {module.level}
                    </span>
                  </div>

                  {/* Topics */}
                  <div className="mb-4">
                    <div className="text-xs font-medium text-gray-500 mb-2">
                      {lang === 'ar' ? 'المواضيع' : 'Topics Covered'}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {(lang === 'ar' ? module.topicsAr : module.topics).map((t, i) => (
                        <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <CheckCircle className="w-3 h-3 text-green-500" />
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Applicable Roles */}
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1">
                      {lang === 'ar' ? 'ينطبق على' : 'Applicable Roles'}
                      <span className="ml-1 text-gray-400">({module.roles.length})</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {module.roles.map(roleId => {
                        const rolePath = ROLE_PATHS.find(p => p.roleId === roleId);
                        if (!rolePath) return null;
                        return (
                          <span key={roleId} className={`text-xs px-2 py-0.5 rounded-full ${groupBadgeColors[rolePath.group]}`}>
                            {lang === 'ar' ? rolePath.roleTitleAr : rolePath.roleTitle}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── CERTIFICATIONS TAB ── */}
        {activeTab === 'certifications' && (
          <div className="space-y-6">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-amber-900 mb-2 flex items-center gap-2">
                <Award className="w-5 h-5" />
                {lang === 'ar' ? 'برنامج الشهادات والمؤهلات' : 'Certification & Badge Program'}
              </h2>
              <p className="text-amber-800 text-sm">
                {lang === 'ar'
                  ? 'تُمنح الشهادات عند إتمام جميع الوحدات المطلوبة لكل مسار وظيفي.'
                  : 'Certifications are awarded upon completion of all required modules for each role path.'}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {ROLE_PATHS.map(path => (
                <div key={path.roleId} className={`bg-white border border-gray-200 rounded-xl p-5 ${groupColors[path.group]}`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900">
                      {lang === 'ar' ? path.roleTitleAr : path.roleTitle}
                    </h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${groupBadgeColors[path.group]}`}>
                      {path.group.toUpperCase()}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {path.certifications.map((cert, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-100">
                        <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                          <Award className="w-4 h-4 text-amber-600" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {lang === 'ar' ? path.certificationsAr[i] : cert}
                          </div>
                          <div className="text-xs text-gray-500">
                            {lang === 'ar' ? 'عند إتمام المسار' : 'Awarded on path completion'}
                          </div>
                        </div>
                        <CheckCircle className="w-4 h-4 text-green-500 ml-auto" />
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-sm text-gray-500">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{path.totalHours}h {lang === 'ar' ? 'إجمالي' : 'total'}</span>
                    <span>{path.modules.length} {lang === 'ar' ? 'وحدة' : 'modules'}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Certification Principles */}
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6">
              <h3 className="font-semibold text-indigo-900 mb-3 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                {lang === 'ar' ? 'مبادئ برنامج الشهادات' : 'Certification Program Principles'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { en: 'All modules must be completed to earn certification', ar: 'يجب إتمام جميع الوحدات للحصول على الشهادة' },
                  { en: 'Annual recertification required for compliance modules', ar: 'تجديد سنوي للشهادات مطلوب لوحدات الامتثال' },
                  { en: 'Certifications tracked in the HR system by CRM Admin', ar: 'تتبع الشهادات في نظام الموارد البشرية بواسطة مدير CRM' },
                  { en: 'Role promotion linked to completion of relevant path', ar: 'الترقية الوظيفية مرتبطة بإتمام المسار ذي الصلة' }
                ].map((p, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-indigo-800">
                    <CheckCircle className="w-4 h-4 text-indigo-500 mt-0.5 shrink-0" />
                    {lang === 'ar' ? p.ar : p.en}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainingResources;
